import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import am4themes_dark from '@amcharts/amcharts4/themes/dark';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import am4themes_material from '@amcharts/amcharts4/themes/material';
import {Align} from '@amcharts/amcharts4/core';
import {Legend} from '@amcharts/amcharts4/charts';

export const ChartThemes = {
  DARK: am4themes_dark,
  ANIMATED: am4themes_animated,
  MATERIAL: am4themes_material
} as const;

export type ChartTheme = keyof typeof ChartThemes;

export class AmchartsHelper {
  public static useTheme(theme: ChartTheme) {
    am4core.useTheme(ChartThemes[theme]);
  }

  public static createLineSeries(category: string,
                                 field: string,
                                 name: string,
                                 chart: am4charts.XYChart,
                                 fillColor: string = '#fff',
                                 onAxis = null,
                                 addLabel: boolean = true) {
    const series = chart.series.push(new am4charts.LineSeries());

    series.dataFields.valueY = field;
    series.dataFields.categoryX = category;
    series.name = name;
    series.stroke = am4core.color(fillColor);
    series.strokeWidth = 3;
    series.connect = false;
    series.tensionX = 0.8;

    const circleBullet = series.bullets.push(new am4charts.CircleBullet());
    circleBullet.circle.stroke = am4core.color('#fff');
    circleBullet.circle.strokeWidth = 3;
    circleBullet.circle.radius = 4;
    circleBullet.circle.fill = am4core.color(fillColor);

    if (onAxis != null) {
      series.yAxis = onAxis;
    }

    if (addLabel) {
      this.addLabelToSeries(series, null, 15);
    }
  }

  public static createSeries(category: string,
                             field: string,
                             name: string,
                             chart: am4charts.XYChart,
                             fillColor: string = '#fff',
                             onAxis = null,
                             addLabel: boolean = true,
                             labelUnit: string = null) {
    const series = chart.series.push(new am4charts.ColumnSeries());

    series.dataFields.valueY = field;
    series.dataFields.categoryX = category;
    series.name = name;
    series.sequencedInterpolation = true;
    series.stacked = false;
    series.strokeWidth = 0;
    series.columns.template.width = am4core.percent(100);
    series.columns.template.fill = am4core.color(fillColor);

    series.padding(35, 0, 20, 0);

    if (onAxis != null) {
      series.yAxis = onAxis;
    }

    if (addLabel) {
      this.addLabelToSeries(series, labelUnit);
    }

    return series;
  }

  public static addCategoryAxis(chart: am4charts.XYChart, category: string, tooltipPrefix: string = null, rotateLabels: boolean = true) {
    const newAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    newAxis.dataFields.category = category;

    if (rotateLabels) {
      newAxis.renderer.cellStartLocation = 0.2;
      newAxis.renderer.cellEndLocation = 0.8;
      newAxis.renderer.labels.template.rotation = -90;
      newAxis.renderer.labels.template.horizontalCenter = 'right';
      newAxis.renderer.labels.template.verticalCenter = 'middle';
      newAxis.renderer.minGridDistance = 30;
    }

    if (tooltipPrefix !== null) {
      newAxis.adapter.add('getTooltipText', (text) => `${tooltipPrefix}: ${text}`);
    }

    return newAxis;
  }

  public static addHorizontalScrollbar(toChart: am4charts.XYChart) {
    toChart.scrollbarX = new am4core.Scrollbar();
  }

  public static addVerticalScrollbar(toChart: am4charts.XYChart) {
    toChart.scrollbarY = new am4core.Scrollbar();
  }

  public static setAxisTooltipPrefix(axis: am4charts.ValueAxis, prefix: string) {
    axis.adapter.add('getTooltipText', (text) => `${prefix}: ${text}`);
  }

  public static addLabelToSeries(series: am4charts.Series,
                                 labelUnit: string | null = null,
                                 valueOffset: number = 0,
                                 categoryOffset: number = -15) {
    const categoryLabel = series.bullets.push(new am4charts.LabelBullet());
    categoryLabel.label.text = '{name}';
    categoryLabel.label.truncate = false;
    categoryLabel.label.dy = categoryOffset;
    categoryLabel.label.wrap = true;
    categoryLabel.label.fontSize = 12;

    const valueLabel = series.bullets.push(new am4charts.LabelBullet());

    if (labelUnit !== null) {
      valueLabel.label.text = '{valueY} ' + labelUnit;
    } else {
      valueLabel.label.text = '{valueY}';
    }

    valueLabel.label.truncate = false;
    valueLabel.label.dy = valueOffset;
    valueLabel.label.wrap = true;
    valueLabel.label.fontSize = 16;
  }

  public static addValueAxis(toChart: am4charts.XYChart,
                             title: string,
                             showGrid = false,
                             rightAlign = false,
                             keepSelection = false,
                             syncWith: am4charts.ValueAxis = null) {
    const valueAxis = toChart.yAxes.push(new am4charts.ValueAxis());

    valueAxis.title.text = title;
    valueAxis.renderer.opposite = rightAlign;
    valueAxis.renderer.grid.template.disabled = !showGrid;
    valueAxis.keepSelection = keepSelection;

    if (syncWith != null) {
      valueAxis.syncWithAxis = syncWith;
    }

    return valueAxis;
  }

  public static addStaticInfoBox(toChart: am4charts.XYChart) {
    const info = toChart.plotContainer.createChild(am4core.Container);

    info.id = 'infoBox';
    info.width = 400;
    info.minHeight = 60;
    info.x = 0;
    info.y = 0;
    info.padding(10, 10, 10, 10);
    info.background.fill = am4core.color('#000');
    info.background.fillOpacity = 0.1;
    info.layout = 'grid';
    info.zIndex = 100;

    return info;
  }

  public static createLabelInBox(box: am4core.Container, field: string, title: string) {
    const titleLabel = box.createChild(am4core.Label);
    titleLabel.text = title + ':';
    titleLabel.marginRight = 20;
    titleLabel.minWidth = 100;

    const valueLabel = box.createChild(am4core.Label);
    valueLabel.id = field;
    valueLabel.text = '-';
    valueLabel.minWidth = 100;
    valueLabel.marginRight = 30;
    valueLabel.fontWeight = 'bolder';
  }

  public static torchify(chart: am4charts.XYChart,
                         centerTitleOnExport = false,
                         centerLegendOnExport = false) {
    // Sorry amCharts but we broke AF
    chart.logo.disabled = true;

    const disabledTooltips: any[] = [];
    const disabledAxesLabels: any[] = [];
    const positionedTitles: {titleText: string; previousPosition: Align}[] = [];

    const previousLegend: Legend = Object.assign({}, chart.legend);
    const watermark = chart.createChild(am4core.Label);
    watermark.text = 'Source: [bold]torchbi.com[/]';
    watermark.align = 'right';
    watermark.fillOpacity = 0.5;
    watermark.opacity = 0.0;
    watermark.marginRight = 10;
    watermark.id = 'watermark';

    // Enable watermark on export
    chart.exporting.events.on('exportstarted', () => {
      chart.htmlContainer.style.opacity = '0.0';

      const infoBox: am4core.Container = chart.plotContainer.children.values.find(s => s.id === 'infoBox') as am4core.Container;

      chart.series.each(s => {
        if (!!s.tooltip && !s.tooltip.disabled) {
          s.tooltip.disabled = true;
        } else if (!!s.tooltip) {
          disabledTooltips.push(s.tooltip);
        }
      });

      if (centerLegendOnExport && !!chart.legend) {
        chart.legend.contentAlign = 'center';
        chart.legend.align = 'center';
        chart.legend.paddingRight = 0;
        chart.legend.paddingTop = 10;
      }

      if (centerTitleOnExport) {
        chart.titles.each(title => {
          if (title.align !== 'center') {
            positionedTitles.push({
              titleText: title.text,
              previousPosition: title.align
            });
            title.align = 'center';
          }
        });

        chart.xAxes.each(axes => {
          if (axes.title.disabled) {
            axes.title.disabled = false;
          } else {
            disabledAxesLabels.push(axes.title);
          }
        });
      }

      watermark.opacity = 1.0;

      if (infoBox !== undefined) {
        infoBox.opacity = 0.0;
      }

      this.hideScrollbars(chart);
    });

    // Disable watermark when export finishes
    chart.exporting.events.on('exportfinished', () => {
      const infoBox: am4core.Container = chart.plotContainer.children.values.find(s => s.id === 'infoBox') as am4core.Container;

      chart.htmlContainer.style.opacity = '1.0';
      watermark.opacity = 0.0;

      chart.series.each(s => {
        if (!!s.tooltip && s.tooltip.disabled && !disabledTooltips.includes(s.tooltip)) {
          s.tooltip.disabled = false;
        }
      });

      chart.xAxes.each(axes => {
        if (axes.title.disabled && !disabledAxesLabels.includes(axes.title)) {
          axes.title.disabled = false;
        }
      });

      if (centerTitleOnExport) {
        chart.titles.each(title => {
          const positionedTitle = positionedTitles.find(t => t.titleText === title.text);
          if (!!positionedTitle) {
            title.align = positionedTitle.previousPosition;
          }
        });
      }

      if (!!chart.legend && centerLegendOnExport && !!previousLegend) {
        chart.legend.contentAlign = previousLegend.contentAlign;
        chart.legend.align = previousLegend.align;
        chart.legend.paddingLeft = previousLegend.paddingLeft;
        chart.legend.paddingRight = previousLegend.paddingRight;
        chart.legend.paddingTop = previousLegend.paddingTop;
      }

      if (infoBox !== undefined) {
        infoBox.opacity = 1.0;
      }

      this.showScrollbars(chart);
    });

    chart.exporting.validateSprites.push(watermark);
  }

  public static async getBlob(chart: am4charts.Chart) {
    return new Promise<string>(resolve => {
      chart.exporting.dispatchImmediately('exportstarted');
      chart.exporting.getImage('png').then(imgData => {
        chart.exporting.dispatchImmediately('exportfinished');
        resolve(imgData);
      });
    });
  }

  public static showScrollbars(onChart: am4charts.XYChart) {
    if (onChart.scrollbarX != null) {
      onChart.scrollbarX.opacity = 1.0;
    }

    if (onChart.scrollbarY != null) {
      onChart.scrollbarY.opacity = 1.0;
    }
  }

  public static hideScrollbars(onChart: am4charts.XYChart) {
    if (onChart.scrollbarX != null) {
      onChart.scrollbarX.opacity = 0.0;
    }

    if (onChart.scrollbarY != null) {
      onChart.scrollbarY.opacity = 0.0;
    }
  }
}
